class ProductGallery {
  constructor(container) {
    this.container = container;
    this.mainGallery = container.querySelector('.product-gallery__main');
    this.thumbsGallery = container.querySelector('.product-gallery__thumbs');

    this.init();
  }

  init() {
    // Get specific navigation buttons for each gallery to prevent conflicts
    const mainNavPrev = this.mainGallery.querySelector('.swiper-button-prev');
    const mainNavNext = this.mainGallery.querySelector('.swiper-button-next');
    const thumbNavPrev = this.thumbsGallery.querySelector('.swiper-button-prev');
    const thumbNavNext = this.thumbsGallery.querySelector('.swiper-button-next');

    // Initialize thumbnail swiper first for better performance
    this.thumbsSwiper = new Swiper(this.thumbsGallery, {
      spaceBetween: 10,
      slidesPerView: 'auto',
      watchSlidesVisibility: true,
      watchSlidesProgress: true,
      centerInsufficientSlides: true,
      freeMode: true,
      watchOverflow: true,
      // Add navigation for thumbnails with specific element references
      navigation: {
        nextEl: thumbNavNext,
        prevEl: thumbNavPrev,
      },
      // Performance optimizations
      resistance: false,
      preventInteractionOnTransition: true,
      // Show/hide navigation based on overflow
      on: {
        init: () => {
          this.updateThumbnailNavigation();
        },
        resize: () => {
          this.updateThumbnailNavigation();
        }
      }
    });

    // Initialize main swiper with mobile-friendly settings
    this.mainSwiper = new Swiper(this.mainGallery, {
      spaceBetween: 0, // Remove space between slides for clean mobile experience
      navigation: {
        nextEl: mainNavNext,
        prevEl: mainNavPrev,
      },
      pagination: {
        el: '.swiper-pagination',
        type: 'bullets',
        clickable: true
      },
      thumbs: {
        swiper: this.thumbsSwiper
      },
      // Mobile-optimized settings
      resistance: true,
      resistanceRatio: 0.85,
      preventInteractionOnTransition: true,
      speed: 300,
      // Ensure slides snap to position properly
      centeredSlides: false,
      slidesPerView: 1,
      // Improve touch behavior
      touchRatio: 1,
      touchAngle: 45,
      grabCursor: true,
      // Prevent partial slide visibility
      watchSlidesProgress: true,
      watchSlidesVisibility: true,
      on: {
        slideChange: () => {
          this.updateActiveThumbnail();
          this.scrollThumbnailIntoView();
          this.handleLazyLoading();
        },
        init: () => {
          this.handleInitialImageLoad();
        },
        // Ensure proper slide positioning after touch end
        touchEnd: (swiper) => {
          // Force slide to snap to proper position
          const activeIndex = swiper.activeIndex;
          setTimeout(() => {
            if (swiper.activeIndex === activeIndex) {
              swiper.slideTo(activeIndex, 200);
            }
          }, 50);
        }
      }
    });

    // Handle thumbnail clicks
    const thumbSlides = this.thumbsGallery.querySelectorAll('.swiper-slide');
    thumbSlides.forEach((slide, index) => {
      slide.addEventListener('click', () => {
        this.mainSwiper.slideTo(index);
      });
    });

    // Handle media type specific initialization
    this.handleMediaTypes();

    // Set initial active thumbnail
    this.updateActiveThumbnail();

    // Handle window resize
    this.handleResize();

    // Preload next image for better perceived performance
    this.preloadNextImage();
  }

  handleInitialImageLoad() {
    // Ensure the first image loads immediately for better LCP
    const firstSlide = this.mainGallery.querySelector('.swiper-slide');
    if (firstSlide) {
      const firstImage = firstSlide.querySelector('img');
      if (firstImage && firstImage.loading === 'lazy') {
        // Override lazy loading for the first image
        firstImage.loading = 'eager';
      }
    }
  }

  handleLazyLoading() {
    // Preload the next and previous images for smoother navigation
    const activeIndex = this.mainSwiper.activeIndex;
    const slides = this.mainGallery.querySelectorAll('.swiper-slide');

    // Preload next image
    if (slides[activeIndex + 1]) {
      const nextImg = slides[activeIndex + 1].querySelector('img');
      if (nextImg && nextImg.loading === 'lazy') {
        nextImg.loading = 'eager';
      }
    }

    // Preload previous image
    if (slides[activeIndex - 1]) {
      const prevImg = slides[activeIndex - 1].querySelector('img');
      if (prevImg && prevImg.loading === 'lazy') {
        prevImg.loading = 'eager';
      }
    }
  }

  preloadNextImage() {
    // Preload the second image for faster navigation
    const slides = this.mainGallery.querySelectorAll('.swiper-slide');
    if (slides.length > 1) {
      const secondImage = slides[1].querySelector('img');
      if (secondImage) {
        // Create a new image element to preload
        const preloadImg = new Image();
        preloadImg.src = secondImage.src;
      }
    }
  }

  updateActiveThumbnail() {
    const activeIndex = this.mainSwiper.activeIndex;
    const thumbSlides = this.thumbsGallery.querySelectorAll('.swiper-slide');

    // Remove active class from all thumbnails
    thumbSlides.forEach(slide => {
      slide.classList.remove('swiper-slide-thumb-active');
    });

    // Add active class to current thumbnail
    if (thumbSlides[activeIndex]) {
      thumbSlides[activeIndex].classList.add('swiper-slide-thumb-active');
    }
  }

  scrollThumbnailIntoView() {
    const activeIndex = this.mainSwiper.activeIndex;
    const thumbSlides = this.thumbsGallery.querySelectorAll('.swiper-slide');
    const activeThumb = thumbSlides[activeIndex];

    if (!activeThumb) return;

    const thumbsContainer = this.thumbsGallery.querySelector('.swiper-wrapper');
    const containerRect = this.thumbsGallery.getBoundingClientRect();
    const thumbRect = activeThumb.getBoundingClientRect();

    // Check if thumbnail is outside visible area
    const isOutsideLeft = thumbRect.left < containerRect.left;
    const isOutsideRight = thumbRect.right > containerRect.right;

    // Only scroll if thumbnail is actually outside the visible area
    if (isOutsideLeft || isOutsideRight) {
      const thumbOffset = activeThumb.offsetLeft;
      const containerWidth = this.thumbsGallery.offsetWidth;
      const thumbWidth = activeThumb.offsetWidth;
      const totalWidth = thumbsContainer.scrollWidth;

      let scrollPosition;

      // For first few thumbnails, align to start
      if (activeIndex === 0 || thumbOffset < containerWidth / 2) {
        scrollPosition = 0;
      }
      // For last few thumbnails, align to end
      else if (activeIndex === thumbSlides.length - 1 || thumbOffset > totalWidth - containerWidth / 2) {
        scrollPosition = totalWidth - containerWidth;
      }
      // For middle thumbnails, center them
      else {
        scrollPosition = thumbOffset - (containerWidth / 2) + (thumbWidth / 2);
      }

      // Ensure we don't scroll beyond boundaries
      const maxScroll = totalWidth - containerWidth;
      scrollPosition = Math.max(0, Math.min(maxScroll, scrollPosition));

      // Convert to negative value for Swiper's setTranslate (Swiper expects negative values)
      const translateValue = -scrollPosition;

      // Use Swiper's setTranslate method for smooth scrolling
      this.thumbsSwiper.setTranslate(translateValue);
      this.thumbsSwiper.updateProgress();
    }
  }

  handleMediaTypes() {
    const slides = this.mainGallery.querySelectorAll('.swiper-slide');

    slides.forEach(slide => {
      // Pause videos when switching slides
      const video = slide.querySelector('video');
      if (video) {
        this.mainSwiper.on('slideChange', () => {
          if (!slide.classList.contains('swiper-slide-active')) {
            video.pause();
          }
        });
      }

      // Handle 3D models
      const model = slide.querySelector('model-viewer');
      if (model) {
        this.mainSwiper.on('slideChange', () => {
          if (slide.classList.contains('swiper-slide-active')) {
            model.dismissPoster();
          }
        });
      }
    });
  }

  handleResize() {
    let resizeTimeout;

    window.addEventListener('resize', () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        // Store current active index
        const currentActiveIndex = this.mainSwiper ? this.mainSwiper.activeIndex : 0;

        // Temporarily destroy and recreate thumbs swiper to fix width issues
        if (this.thumbsSwiper) {
          this.thumbsSwiper.destroy(true, true);
        }

        // Get navigation buttons for proper targeting
        const thumbNavPrev = this.thumbsGallery.querySelector('.swiper-button-prev');
        const thumbNavNext = this.thumbsGallery.querySelector('.swiper-button-next');

        // Reinitialize thumbnail swiper with proper settings
        this.thumbsSwiper = new Swiper(this.thumbsGallery, {
          spaceBetween: 10,
          slidesPerView: 'auto',
          watchSlidesVisibility: true,
          watchSlidesProgress: true,
          centerInsufficientSlides: true,
          freeMode: true,
          watchOverflow: true,
          navigation: {
            nextEl: thumbNavNext,
            prevEl: thumbNavPrev,
          },
          resistance: false,
          preventInteractionOnTransition: true,
          on: {
            init: () => {
              this.updateThumbnailNavigation();
              // Ensure the main swiper's thumbs are reconnected
              if (this.mainSwiper && this.mainSwiper.thumbs) {
                this.mainSwiper.thumbs.swiper = this.thumbsSwiper;
              }
              // Restore the active index
              this.mainSwiper.slideTo(currentActiveIndex, 0);
            },
            resize: () => {
              this.updateThumbnailNavigation();
            }
          }
        });

        // Reconnect thumbnail clicks
        const thumbSlides = this.thumbsGallery.querySelectorAll('.swiper-slide');
        thumbSlides.forEach((slide, index) => {
          slide.addEventListener('click', () => {
            this.mainSwiper.slideTo(index);
          });
        });

        // Update main swiper
        if (this.mainSwiper) {
          this.mainSwiper.update();
        }

        // Recalculate thumbnail positioning
        this.updateActiveThumbnail();
        this.scrollThumbnailIntoView();
        this.updateThumbnailNavigation();

        // Force layout recalculation
        this.forceLayoutRecalculation();
      }, 250);
    });
  }

  updateThumbnailNavigation() {
    // Check if thumbnails overflow and show/hide navigation accordingly
    const thumbsContainer = this.thumbsGallery;
    const thumbsWrapper = thumbsContainer.querySelector('.swiper-wrapper');
    const prevBtn = thumbsContainer.querySelector('.swiper-button-prev');
    const nextBtn = thumbsContainer.querySelector('.swiper-button-next');

    if (!thumbsWrapper || !prevBtn || !nextBtn) return;

    const containerWidth = thumbsContainer.offsetWidth;
    const wrapperWidth = thumbsWrapper.scrollWidth;
    const hasOverflow = wrapperWidth > containerWidth;

    if (hasOverflow) {
      prevBtn.style.display = 'flex';
      nextBtn.style.display = 'flex';
      thumbsContainer.classList.add('has-navigation');
    } else {
      prevBtn.style.display = 'none';
      nextBtn.style.display = 'none';
      thumbsContainer.classList.remove('has-navigation');
    }
  }

  forceLayoutRecalculation() {
    // Force browser to recalculate layouts
    const thumbSlides = this.thumbsGallery.querySelectorAll('.swiper-slide');
    thumbSlides.forEach(slide => {
      // Trigger reflow by accessing offsetHeight
      slide.style.width = '';
      const height = slide.offsetHeight;
      // Reset any inline styles that might interfere
      slide.style.width = '60px'; // Set explicit width
    });

    // Force wrapper recalculation
    const wrapper = this.thumbsGallery.querySelector('.swiper-wrapper');
    if (wrapper) {
      wrapper.style.transform = '';
      // Trigger reflow
      const height = wrapper.offsetHeight;
    }
  }

  destroy() {
    if (this.mainSwiper) {
      this.mainSwiper.destroy();
    }
    if (this.thumbsSwiper) {
      this.thumbsSwiper.destroy();
    }
  }
}

// Make the class globally available
window.ProductGallery = ProductGallery;

// Function to initialize a specific gallery element
window.initializeProductGallery = function (element) {
  if (element) {
    new ProductGallery(element);
  }
};

// Initialize all galleries on DOMContentLoaded
document.addEventListener('DOMContentLoaded', function () {
  const productGalleries = document.querySelectorAll('.product-gallery-with-thumbs');
  productGalleries.forEach(gallery => {
    if (!gallery.classList.contains('initialized')) { // Prevent re-initialization
      new ProductGallery(gallery);
      gallery.classList.add('initialized');
    }
  });
});

// Expose a function to re-initialize galleries, useful for dynamically loaded content
window.reinitializeProductGalleries = function () {
  const productGalleries = document.querySelectorAll('.product-gallery-with-thumbs:not(.initialized)');
  productGalleries.forEach(gallery => {
    new ProductGallery(gallery);
    gallery.classList.add('initialized');
  });
};